import { useState, useEffect, useRef } from 'react';
import api from '../api';
import CompetitorTable from '../components/CompetitorTable';

const BG = '#0a0f1a';
const SURFACE = '#111827';
const BORDER = 'rgba(255,255,255,0.08)';
const MY_COLOR = '#1565C0';
const COMP_COLOR = '#2E7D32';
const GOOD = 'rgba(16,185,129,0.15)';
const BAD = 'rgba(239,68,68,0.15)';

function extractNm(input) {
  const str = String(input).trim();
  const match = str.match(/\/catalog\/(\d+)\//);
  if (match) return parseInt(match[1]);
  if (/^\d{8,12}$/.test(str)) return parseInt(str);
  return null;
}

function seoWords(text) {
  const stopWords = new Set(['что','это','как','все','при','для','или','если','после','также','так','ваш','наш','вам','нам','его','её','они','этот','этой','этим','этих','которые','который','которая','может','очень','более','менее','такой','такое','такая','будет','были','было','есть','имеет','этом','этого','свой','своя','своё','свои','без','под','над','перед','через','около','среди','между','кроме','против','вокруг','прежде','давно','долго','снова','тоже','уже','даже','лишь','хотя','когда','зачем','почему','просто','должен','нет','не','на','в','с','по','из','до','от','за','со','во','об','к','о','у','а','и','но','да','же']);
  return text.toLowerCase().replace(/[^а-яёa-z\s]/gi,' ').split(/\s+/).filter(w => w.length > 3 && !stopWords.has(w));
}

function AuditDetail({ audit, onBack, onSave, myArticles }) {
  const [selectedNms, setSelectedNms] = useState(() => {
    const ids = Array.isArray(audit.nm_ids) && audit.nm_ids.length
      ? audit.nm_ids.map(String)
      : (audit.nm_id ? [String(audit.nm_id)] : []);
    return ids;
  });
  const [competitors, setCompetitors] = useState(() => { try { const s=sessionStorage.getItem('audit_comp_'+audit.id); if(s) return JSON.parse(s); } catch {} return audit.competitors?.length ? audit.competitors : ['','','','','','','','','','']; });
  const [cards, setCards] = useState(() => audit.cards || []);
  const [mpData, setMpData] = useState(audit.mp_data || {});
  const mpSaveTimer = useRef(null);
  const latestCompState = useRef(audit.competitor_state || {});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('audit_nms_'+audit.id, JSON.stringify(selectedNms));
    const ids = selectedNms.filter(Boolean).map(Number);
    if (!ids.length) return;
    if (mpSaveTimer.current) clearTimeout(mpSaveTimer.current);
    mpSaveTimer.current = setTimeout(() => {
      onSave({ ...audit, nm_ids: ids });
    }, 2000);
  }, [selectedNms]);
  useEffect(() => { sessionStorage.setItem('audit_comp_'+audit.id, JSON.stringify(competitors)); }, [competitors, audit.id]);
  useEffect(() => {
    if (cards.length) sessionStorage.setItem('audit_cards_'+audit.id, JSON.stringify(cards));
  }, [cards, audit.id]);

  // Всегда определяем свои товары по реальному списку myArticles,
  // а не по позиции карточки.
  useEffect(() => {
    if (!myArticles?.length || !cards.length) return;
    const ownIds = new Set(myArticles.map(a => String(a.nm_id)));
    setCards(prev => prev.map(c => ({
      ...c,
      isOwn: String(c.nmId) === String(audit.nm_id) || ownIds.has(String(c.nmId))
    })));
  }, [myArticles]);
  const [progress, setProgress] = useState('');
  const [tab, setTab] = useState('funnel');
  const [collapsedFunnel, setCollapsedFunnel] = useState(true);

  const analyze = async () => {
    setLoading(true); setCards([]); setProgress('');
    const nms = selectedNms.filter(v => v && String(v).trim()).map(v => parseInt(String(v).trim())).filter(v => !isNaN(v) && v > 1000000);
    competitors.forEach(v => { const nm = extractNm(v); if (nm) nms.push(nm); });
    const results = [];
    for (let i = 0; i < nms.length; i++) {
      setProgress(`Загружаем ${i+1} из ${nms.length}...`);
      try { const r = await api.get('/audit/card/' + nms[i]); results.push({
          ...r.data,
          isOwn: String(nms[i]) === String(audit.nm_id) || myArticles?.some(a => String(a.nm_id) === String(nms[i])) === true
        }); }
      catch(e) {
        results.push({
          nmId: nms[i],
          error: true,
          isOwn: String(nms[i]) === String(audit.nm_id) || myArticles?.some(a => String(a.nm_id) === String(nms[i])) === true
        });
      }
    }
    setCards(results);
    setLoading(false); setProgress('');
    // Автозаполняем price_wb из карточек
    const priceUpdates = {};
    results.forEach(r => { if (r.nmId && r.price) priceUpdates['price_wb_'+r.nmId] = String(r.price); });
    // Загружаем отзывы для наших артикулов
    const reviewUpdates = {};
    const ourResults = results.filter(r => r.isOwn);
    for (const r of ourResults) {
      try {
        const rv = await api.get('/audit/reviews/' + r.nmId);
        if (rv.data?.formatted) reviewUpdates['reviews5_'+r.nmId] = rv.data.formatted;
      } catch(e) { console.error('reviews error:', r.nmId, e.message); }
    }
    const newMpData = {...mpData, ...priceUpdates, ...reviewUpdates};
    if (Object.keys(priceUpdates).length || Object.keys(reviewUpdates).length) setMpData(newMpData);
    onSave({ ...audit, mp_data: newMpData, nm_ids: selectedNms.filter(Boolean).map(Number), nm_id: selectedNms.filter(Boolean)[0] ? parseInt(selectedNms.filter(Boolean)[0]) : null, competitors, cards: results });
  };

  const getVC = (nmId) => myArticles?.find(a => String(a.nm_id) === String(nmId))?.vendor_code || '';
  // Сортируем cards по порядку selectedNms
  // Собственный товар аудита всегда первый.
  const ownNmId = audit.nm_id ? String(audit.nm_id) : null;

  const displayCards = [...cards];

  if (ownNmId) {
    const ownIndex = displayCards.findIndex(
      c => String(c.nmId) === ownNmId
    );

    if (ownIndex >= 0) {
      displayCards[ownIndex] = {
        ...displayCards[ownIndex],
        isOwn: true
      };

      const ownCard = displayCards.splice(ownIndex, 1)[0];
      displayCards.unshift(ownCard);

    } else {
      displayCards.unshift({
        nmId: Number(ownNmId),
        isOwn: true,
        error: false,
        name: 'Мой товар',
        vendorCode: '',
        photos: [],
        chars: {},
        description: ''
      });
    }
  }

  const sortedCards = [...displayCards].sort((a, b) => {
    if (a.isOwn && !b.isOwn) return -1;
    if (!a.isOwn && b.isOwn) return 1;

    const ai = selectedNms.indexOf(String(a.nmId));
    const bi = selectedNms.indexOf(String(b.nmId));

    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;

    return ai - bi;
  });
  const allChars = [...new Set(sortedCards.flatMap(c => Object.keys(c.chars || {})))];

  const wordFreq = {};
  const wordSets = cards.map(c => {
    const words = seoWords(c.description||'');
    const set = new Set(words);
    words.forEach(w => { wordFreq[w] = (wordFreq[w]||0) + 1; });
    return set;
  });
  const topWords = Object.entries(wordFreq).filter(([,cnt]) => cnt >= 2 || cards.length === 1).sort((a,b) => b[1]-a[1]).slice(0,300);
  const COL_W = 150, LABEL_W = 200;

  return (
    <div style={{minHeight:'100vh',background:BG,color:'#fff',padding:24}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
        <button onClick={()=>{sessionStorage.removeItem('audit_current_id');onBack();}} style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',color:'#fff',padding:'6px 14px',borderRadius:8,cursor:'pointer',fontSize:13}}>← Назад</button>
        <button onClick={async ()=>{
          try {
            const r = await api.get('/audit/export/'+audit.id, {responseType:'blob'});
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a'); a.href=url; a.download=audit.name+'.xlsx'; a.click();
            URL.revokeObjectURL(url);
          } catch(e) { alert('Ошибка экспорта'); }
        }} style={{background:'rgba(16,185,129,0.2)',border:'1px solid rgba(16,185,129,0.4)',color:'#10b981',fontSize:13,padding:'8px 16px',borderRadius:8,cursor:'pointer',fontWeight:600}}>
          📥 Excel
        </button>
        <h1 style={{fontSize:20,fontWeight:700,margin:0}}>🔍 {audit.name}</h1>
      </div>

      <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:16,marginBottom:20}}>
        <div style={{display:'flex',flexWrap:'wrap',gap:16,alignItems:'flex-start'}}>
          <div>
            <div style={{fontSize:11,color:'rgba(200,215,235,0.5)',marginBottom:6}}>⭐ Мои артикулы:</div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {selectedNms.map((nm,i) => (
                <div key={i} style={{display:'flex',gap:6,alignItems:'center'}}>
                  <select value={nm} onChange={e=>{const a=[...selectedNms];a[i]=e.target.value;setSelectedNms(a);}}
                    style={{background:'#0a0f1a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:6,color:'#fff',fontSize:12,padding:'6px 10px',width:250}}>
                    <option value="">— Выбрать —</option>
                    {myArticles.map(a => <option key={a.nm_id} value={a.nm_id}>{a.vendor_code} ({a.nm_id})</option>)}
                  </select>
                  <button onClick={()=>setSelectedNms(prev=>prev.filter((_,j)=>j!==i))}
                    style={{background:'transparent',border:'none',color:'rgba(239,68,68,0.6)',fontSize:16,cursor:'pointer'}}>✕</button>
                </div>
              ))}
              <button onClick={()=>setSelectedNms(prev=>[...prev,''])}
                style={{background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.3)',color:'#60a5fa',fontSize:11,padding:'4px 10px',borderRadius:6,cursor:'pointer',width:'fit-content'}}>
                + Добавить артикул
              </button>
            </div>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:'rgba(200,215,235,0.5)',marginBottom:6}}>🏁 Конкуренты (ссылка или nmId, до 10):</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {competitors.map((v,i) => (
                <input key={i} value={v} onChange={e=>{const c=[...competitors];c[i]=e.target.value;setCompetitors(c);}}
                  placeholder={`Конкурент ${i+1}`}
                  style={{background:'#0a0f1a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:6,color:'#fff',fontSize:11,padding:'5px 8px',width:200}}/>
              ))}
            </div>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8,justifyContent:'flex-end'}}>
            <button onClick={analyze} disabled={loading}
              style={{background:'rgba(59,130,246,0.2)',border:'1px solid rgba(59,130,246,0.4)',color:'#60a5fa',fontSize:13,padding:'8px 24px',borderRadius:8,cursor:'pointer',fontWeight:700}}>
              {loading ? `⟳ ${progress}` : '▶ Анализировать'}
            </button>
            {sortedCards.length > 0 && <button onClick={()=>setCards([])}
              style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',color:'rgba(200,215,235,0.5)',fontSize:11,padding:'5px 12px',borderRadius:6,cursor:'pointer'}}>
              🧹 Очистить
            </button>}
          </div>
        </div>
      </div>

      {sortedCards.length > 0 && (
        <>
          <div style={{overflowX:'auto'}}>
          <div style={{display:'flex',gap:4,marginBottom:16,alignItems:'center'}}>
            <button onClick={()=>setCollapsedFunnel(v=>!v)} style={{background:'transparent',border:'1px solid rgba(255,255,255,0.1)',color:'rgba(200,215,235,0.5)',fontSize:11,padding:'6px 10px',borderRadius:8,cursor:'pointer'}}>{collapsedFunnel?'▶ Развернуть':'▼ Свернуть'}</button>
            {[{k:'funnel',l:'📸 Фото воронка'},{k:'chars',l:'📊 Характеристики'},{k:'seo',l:'🔍 SEO Анализ'}].map(t => (
              <button key={t.k} onClick={()=>setTab(t.k)} style={{
                padding:'8px 20px',fontSize:12,fontWeight:tab===t.k?700:400,borderRadius:8,cursor:'pointer',
                background:tab===t.k?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.04)',
                border:tab===t.k?'1px solid rgba(59,130,246,0.5)':'1px solid rgba(255,255,255,0.1)',
                color:tab===t.k?'#60a5fa':'rgba(200,215,235,0.6)',
              }}>{t.l}</button>
            ))}
          </div>

          {tab === 'funnel' && collapsedFunnel && (
            <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:'auto'}}>
              <table style={{borderCollapse:'collapse',tableLayout:'fixed',minWidth:LABEL_W + Math.max(sortedCards.length,6)*COL_W}}>
                <thead>
                  <tr style={{background:'#1a1a2e'}}>
                    <th style={{width:LABEL_W,padding:'8px 16px',textAlign:'left',fontSize:11,color:'rgba(200,215,235,0.5)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:'#1a1a2e',wordBreak:'break-word',whiteSpace:'normal'}}>Артикул</th>
                    {sortedCards.map((c,i) => <th key={i} style={{width:COL_W,padding:'10px 8px',textAlign:'center',fontSize:11,fontWeight:700,background:c.isOwn?MY_COLOR:COMP_COLOR,color:'#fff',borderRight:`1px solid ${BORDER}`,wordBreak:'break-word',whiteSpace:'normal'}}>{c.isOwn?'⭐ МОЙ':'🏁 К'+(i-sortedCards.filter((x,j)=>j<i&&x.isOwn).length+1)}<br/><span style={{fontSize:9,opacity:0.7}}>{getVC(c.nmId)}<br/>Арт. {c.nmId}</span></th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{borderBottom:`1px solid ${BORDER}`}}>
                    <td style={{padding:'8px 12px',fontSize:11,fontWeight:600,color:'rgba(200,215,235,0.7)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:SURFACE,wordBreak:'break-word',whiteSpace:'normal'}}>🖼️ Главное фото</td>
                    {sortedCards.map((c,i) => <td key={i} style={{padding:8,textAlign:'center',borderRight:`1px solid ${BORDER}`}}>{c.photos?.[0]?<img src={c.photos[0]} alt="" style={{width:120,height:120,objectFit:'cover',borderRadius:6}}/>:'—'}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {tab === 'funnel' && !collapsedFunnel && (
            <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:'auto'}}>
              <table style={{borderCollapse:'collapse',tableLayout:'fixed',minWidth:LABEL_W + Math.max(sortedCards.length,6)*COL_W}}>
                <thead>
                  <tr style={{background:'#1a1a2e'}}>
                    <th style={{width:LABEL_W,padding:'8px 16px',textAlign:'left',fontSize:11,color:'rgba(200,215,235,0.5)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:'#1a1a2e',wordBreak:'break-word',whiteSpace:'normal'}}>ПАРАМЕТР / ФОТО</th>
                    {sortedCards.map((c,i) => <th key={i} style={{width:COL_W,padding:'10px 8px',textAlign:'center',fontSize:11,fontWeight:700,background:c.isOwn?MY_COLOR:COMP_COLOR,color:'#fff',borderRight:`1px solid ${BORDER}`,wordBreak:'break-word',whiteSpace:'normal'}}>{c.isOwn?'⭐ МОЙ ТОВАР':'🏁 Конкурент '+(i - sortedCards.filter((x,j)=>j<i&&x.isOwn).length + 1)}<br/><span style={{fontSize:9,opacity:0.7}}>{getVC(c.nmId)}<br/>Арт. {c.nmId}</span></th>)}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {l:'📌 Название',fn:c=>c.name||'—'},
                    {l:'💰 Цена (₽)',fn:c=>c.price?c.price+' ₽':'—'},
                    {l:'📷 Кол-во фото',fn:c=>c.photoCount!=null?c.photoCount+' шт.':'—',colorFn:(v,all)=>{const nums=all.map(x=>parseInt(x)).filter(x=>!isNaN(x));const mx=Math.max(...nums);const n=parseInt(v);return isNaN(n)?null:n===mx?GOOD:BAD;}},
                    {l:'📂 Категория',fn:c=>c.category||'—'},
                    {l:'🏷️ Бренд',fn:c=>c.brand||'—'},
                  ].map((row,ri) => (
                    <tr key={ri} style={{borderBottom:`1px solid ${BORDER}`}}>
                      <td style={{padding:'8px 12px',fontSize:11,fontWeight:600,color:'rgba(200,215,235,0.7)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:SURFACE,wordBreak:'break-word',whiteSpace:'normal'}}>{row.l}</td>
                      {sortedCards.map((c,i) => { const val=c.error?'❌ Ошибка':row.fn(c); const bg=row.colorFn?row.colorFn(val,cards.map(x=>x.error?'0':row.fn(x))):null; return <td key={i} style={{padding:'8px',fontSize:11,textAlign:'center',background:bg||'transparent',borderRight:`1px solid ${BORDER}`}}>{val}</td>; })}
                    </tr>
                  ))}
                  <tr style={{borderBottom:`1px solid ${BORDER}`,background:'rgba(59,130,246,0.05)'}}>
                    <td style={{padding:'8px 12px',fontSize:11,fontWeight:600,color:'rgba(200,215,235,0.7)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:'rgba(59,130,246,0.05)',wordBreak:'break-word',whiteSpace:'normal'}}>🖼️ Главное фото</td>
                    {sortedCards.map((c,i) => <td key={i} style={{padding:8,textAlign:'center',borderRight:`1px solid ${BORDER}`}}>{c.photos?.[0]?<img src={c.photos[0]} alt="" style={{width:180,height:180,objectFit:'cover',borderRadius:6}}/>:'—'}</td>)}
                  </tr>
                  {Array.from({length:15},(_,j)=>j+1).map(idx => {
                    if (!cards.some(c=>c.photos?.[idx])) return null;
                    return <tr key={idx} style={{borderBottom:`1px solid ${BORDER}`}}><td style={{padding:'8px 12px',fontSize:11,color:'rgba(200,215,235,0.5)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:SURFACE,wordBreak:'break-word',whiteSpace:'normal'}}>Фото {idx+1}</td>{sortedCards.map((c,i)=><td key={i} style={{padding:8,textAlign:'center',borderRight:`1px solid ${BORDER}`}}>{c.photos?.[idx]?<img src={c.photos[idx]} alt="" style={{width:180,height:180,objectFit:'cover',borderRadius:6}}/>:<span style={{color:'rgba(200,215,235,0.2)'}}>—</span>}</td>)}</tr>;
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'chars' && (
            <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:'auto'}}>
              <table style={{borderCollapse:'collapse',tableLayout:'fixed',minWidth:LABEL_W + Math.max(sortedCards.length,6)*COL_W}}>
                <thead>
                  <tr style={{background:'#1a1a2e'}}>
                    <th style={{width:LABEL_W,padding:'8px 16px',textAlign:'left',fontSize:11,color:'rgba(200,215,235,0.5)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:'#1a1a2e',wordBreak:'break-word',whiteSpace:'normal'}}>ХАРАКТЕРИСТИКА</th>
                    {sortedCards.map((c,i) => <th key={i} style={{width:COL_W,padding:'10px 8px',textAlign:'center',fontSize:11,fontWeight:700,background:c.isOwn?MY_COLOR:COMP_COLOR,color:'#fff',borderRight:`1px solid ${BORDER}`,wordBreak:'break-word',whiteSpace:'normal'}}>{c.isOwn?'⭐ МОЙ ТОВАР':'🏁 Конкурент '+(i - sortedCards.filter((x,j)=>j<i&&x.isOwn).length + 1)}<br/><span style={{fontSize:9,opacity:0.7}}>{getVC(c.nmId)}<br/>Арт. {c.nmId}</span></th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{borderBottom:`1px solid ${BORDER}`}}><td style={{padding:'8px 12px',fontSize:11,fontWeight:600,color:'rgba(200,215,235,0.7)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:SURFACE,wordBreak:'break-word',whiteSpace:'normal'}}>🖼️ Главное фото</td>{sortedCards.map((c,i)=><td key={i} style={{padding:8,textAlign:'center',borderRight:`1px solid ${BORDER}`}}>{c.photos?.[0]?<img src={c.photos[0]} alt="" style={{width:180,height:180,objectFit:'cover',borderRadius:6}}/>:'—'}</td>)}</tr>
                  <tr style={{borderBottom:`1px solid ${BORDER}`}}><td style={{padding:'8px 12px',fontSize:11,fontWeight:600,color:'rgba(200,215,235,0.7)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:SURFACE,wordBreak:'break-word',whiteSpace:'normal'}}>📌 Название</td>{sortedCards.map((c,i)=><td key={i} style={{padding:'8px',fontSize:10,textAlign:'center',background:c.name?GOOD:BAD,borderRight:`1px solid ${BORDER}`}}>{c.name||'—'}</td>)}</tr>
                  <tr style={{borderBottom:`1px solid ${BORDER}`}}><td style={{padding:'8px 12px',fontSize:11,fontWeight:600,color:'rgba(200,215,235,0.7)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:SURFACE,wordBreak:'break-word',whiteSpace:'normal'}}>📝 Описание</td>{sortedCards.map((c,i)=><td key={i} style={{padding:'8px',fontSize:10,textAlign:'center',background:c.description?GOOD:BAD,borderRight:`1px solid ${BORDER}`}}>{c.description?`✅ Есть (${c.description.length} симв.)`:'❌ Нет'}</td>)}</tr>
                  {allChars.map((char,ci) => <tr key={ci} style={{borderBottom:`1px solid ${BORDER}`}}><td style={{padding:'8px 12px',fontSize:11,color:'rgba(200,215,235,0.7)',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:SURFACE,wordBreak:'break-word',whiteSpace:'normal'}}>{char}</td>{sortedCards.map((c,i)=>{const val=c.chars?.[char];return <td key={i} style={{padding:'8px',fontSize:10,textAlign:'center',background:val?GOOD:BAD,borderRight:`1px solid ${BORDER}`}}>{val||'—'}</td>;})}</tr>)}
                  <tr><td style={{padding:'8px 12px',fontSize:11,fontWeight:700,color:'#60a5fa',borderRight:`1px solid ${BORDER}`,position:'sticky',left:0,background:'rgba(59,130,246,0.05)',wordBreak:'break-word',whiteSpace:'normal'}}>📈 Заполненность</td>{sortedCards.map((c,i)=>{const filled=allChars.filter(k=>c.chars?.[k]).length;const pct=allChars.length>0?Math.round(filled/allChars.length*100):0;return <td key={i} style={{padding:'8px',fontSize:12,fontWeight:700,textAlign:'center',background:pct>=80?GOOD:pct>=50?'rgba(234,179,8,0.15)':BAD,borderRight:`1px solid ${BORDER}`}}>{filled}/{allChars.length} ({pct}%)</td>;})}</tr>
                </tbody>
              </table>
            </div>
          )}

          {tab === 'seo' && (
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:'auto'}}>
                <div style={{padding:'12px 16px',borderBottom:`1px solid ${BORDER}`,fontWeight:700,fontSize:13}}>📝 Описания товаров</div>
                <table style={{borderCollapse:'collapse',width:'100%'}}>
                  <thead><tr>{sortedCards.map((c,i)=><th key={i} style={{padding:'8px',textAlign:'center',fontSize:11,fontWeight:700,background:c.isOwn?MY_COLOR:COMP_COLOR,color:'#fff',borderRight:`1px solid ${BORDER}`,wordBreak:'break-word',whiteSpace:'normal'}}>{c.isOwn?'⭐ МОЙ ТОВАР':'🏁 Конкурент '+(i - cards.filter((x,j)=>j<i&&x.isOwn).length + 1)}</th>)}</tr></thead>
                  <tbody><tr>{sortedCards.map((c,i)=><td key={i} style={{padding:12,fontSize:10,verticalAlign:'top',borderRight:`1px solid ${BORDER}`,lineHeight:1.6,maxWidth:300}}>{c.description||'—'}</td>)}</tr></tbody>
                </table>
              </div>
              <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,overflow:'auto'}}>
                <div style={{padding:'12px 16px',borderBottom:`1px solid ${BORDER}`,fontWeight:700,fontSize:13}}>🔑 Ключевые слова</div>
                <table style={{borderCollapse:'collapse',width:'100%'}}>
                  <thead><tr style={{background:'#1a1a2e'}}><th style={{width:40,padding:'6px 8px',fontSize:10,color:'rgba(200,215,235,0.5)',borderRight:`1px solid ${BORDER}`}}>№</th><th style={{width:200,padding:'6px 8px',fontSize:10,color:'rgba(200,215,235,0.5)',textAlign:'left',borderRight:`1px solid ${BORDER}`}}>Слово</th><th style={{width:60,padding:'6px 8px',fontSize:10,color:'rgba(200,215,235,0.5)',borderRight:`1px solid ${BORDER}`}}>Упом.</th>{sortedCards.map((c,i)=><th key={i} style={{width:50,padding:'6px 8px',fontSize:10,textAlign:'center',background:c.isOwn?'rgba(21,101,192,0.3)':'rgba(46,125,50,0.3)',color:'#fff',borderRight:`1px solid ${BORDER}`,wordBreak:'break-word',whiteSpace:'normal'}}>{c.isOwn?'⭐':'К'+i}</th>)}</tr></thead>
                  <tbody>{topWords.map(([word,cnt],wi)=>{const inC=wordSets.filter(s=>s.has(word)).length;const bg=inC===cards.length?GOOD:inC>cards.length/2?'rgba(234,179,8,0.1)':'transparent';return <tr key={wi} style={{borderBottom:`1px solid rgba(255,255,255,0.03)`,background:bg}}><td style={{padding:'4px 8px',fontSize:10,textAlign:'center',color:'rgba(200,215,235,0.4)',borderRight:`1px solid ${BORDER}`}}>{wi+1}</td><td style={{padding:'4px 8px',fontSize:11,borderRight:`1px solid ${BORDER}`}}>{word}</td><td style={{padding:'4px 8px',fontSize:10,textAlign:'center',fontWeight:700,borderRight:`1px solid ${BORDER}`}}>{cnt}</td>{wordSets.map((set,i)=><td key={i} style={{padding:'4px 8px',fontSize:12,textAlign:'center',background:set.has(word)?GOOD:BAD,borderRight:`1px solid ${BORDER}`}}>{set.has(word)?'✅':'❌'}</td>)}</tr>;})}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </div>
        </>
      )}
      <div style={{marginBottom:20}}>
        <CompetitorTable
          key={audit.id}
          auditId={audit.id}
          myArticles={myArticles}
          tableWidth={LABEL_W + Math.max(sortedCards.length, 6) * COL_W}
          cardNmIds={sortedCards.filter(c=>!c.isOwn).map(c=>String(c.nmId))}
          mpData={{...mpData, __comp_nm_ids: audit.mp_comp_nm_ids || []}}
          competitorState={audit.competitor_state || {}}
          onMpDataChange={(newMp, compNmIds) => {
            setMpData(newMp);
            if (mpSaveTimer.current) clearTimeout(mpSaveTimer.current);
            mpSaveTimer.current = setTimeout(() => {
              onSave({ ...audit, mp_data: newMp, mp_comp_nm_ids: compNmIds || [], competitor_state: latestCompState.current || audit.competitor_state || {} });
            }, 2000);
          }}
          onStateChange={(state) => {
            latestCompState.current = state;
            if (mpSaveTimer.current) clearTimeout(mpSaveTimer.current);
            mpSaveTimer.current = setTimeout(() => {
              onSave({ ...audit, competitor_state: state });
            }, 2000);
          }}
        />
      </div>
    </div>
  );
}

export default function Audit() {
  const [audits, setAudits] = useState([]);
  const [current, setCurrent] = useState(null);
  const [myArticles, setMyArticles] = useState([]);
  const [newName, setNewName] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    api.get('/monitoring/audits').then(r => { setAudits(r.data); const savedId = sessionStorage.getItem('audit_current_id'); if (savedId) { const found = r.data.find(a => String(a.id) === savedId); if (found) setCurrent(found); else sessionStorage.removeItem('audit_current_id'); } }).catch(()=>{});
    api.get('/monitoring/unit-articles').then(r => setMyArticles(r.data)).catch(()=>{});
  }, []);

  const createAudit = async () => {
    if (!newName.trim()) return;
    try {
      const r = await api.post('/monitoring/audits', { name: newName.trim(), competitors: [], cards: [] });
      setAudits(prev => [r.data, ...prev]);
      // Сбрасываем данные конкурентов для нового аудита
      localStorage.removeItem('bystats_competitor_data');
      setCurrent(r.data);
      setNewName('');
      setShowModal(false);
    } catch(e) { console.error('Ошибка создания:', e); }
  };

  const saveAudit = async (updated) => {
    try {
      const r = await api.put('/monitoring/audits/' + updated.id, updated);
      const saved = r.data || updated;

      setAudits(prev => prev.map(a => a.id === saved.id ? saved : a));
      setCurrent(prev => {
        if (!prev || String(prev.id) !== String(saved.id)) return prev;
        // Не перезаписываем competitor_state из БД — он может быть устаревшим
        return { ...saved, competitor_state: updated.competitor_state || saved.competitor_state };
      });
    } catch(e) {
      console.error('Ошибка сохранения:', e);
    }
  };

  const deleteAudit = async (id) => {
    await api.delete('/monitoring/audits/' + id);
    setAudits(prev => prev.filter(a => a.id !== id));
  };

  if (current) return <AuditDetail key={current.id} audit={current} onBack={()=>setCurrent(null)} onSave={saveAudit} myArticles={myArticles}/>;

  return (
    <div style={{minHeight:'100vh',background:BG,color:'#fff',padding:24}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <h1 style={{fontSize:20,fontWeight:700,margin:0}}>🔍 Аудит карточки</h1>
        <button onClick={()=>setShowModal(true)}
          style={{background:'rgba(59,130,246,0.2)',border:'1px solid rgba(59,130,246,0.4)',color:'#60a5fa',fontSize:13,padding:'8px 20px',borderRadius:8,cursor:'pointer',fontWeight:700}}>
          + Добавить аудит
        </button>
      </div>

      {audits.length === 0 ? (
        <div style={{textAlign:'center',padding:60,color:'rgba(200,215,235,0.4)'}}>
          <div style={{fontSize:48,marginBottom:16}}>🔍</div>
          <div style={{fontSize:16}}>Нет аудитов. Нажмите «+ Добавить аудит» чтобы начать.</div>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {audits.map(a => (
            <div key={a.id} onClick={async ()=>{
        sessionStorage.setItem('audit_current_id',String(a.id));
        try {
          const fresh = await api.get('/monitoring/audits');
          const found = fresh.data.find(x=>x.id===a.id);
          setCurrent(found || a);
        } catch { setCurrent(a); }
      }}
              style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:12,padding:'16px 20px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between'}}
              onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(59,130,246,0.4)'}
              onMouseLeave={e=>e.currentTarget.style.borderColor=BORDER}>
              <div>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>{a.name}</div>
                <div style={{fontSize:11,color:'rgba(200,215,235,0.4)'}}>
                  {a.cards?.length ? `${a.cards.length} карточек` : 'Пусто'} · {new Date(a.updated_at).toLocaleDateString('ru-RU')}
                </div>
              </div>
              <button onClick={e=>{e.stopPropagation();if(window.confirm('Удалить аудит?'))deleteAudit(a.id);}}
                style={{background:'transparent',border:'none',color:'rgba(239,68,68,0.5)',fontSize:18,cursor:'pointer',padding:'4px 8px',borderRadius:6}}
                onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
                onMouseLeave={e=>e.currentTarget.style.color='rgba(239,68,68,0.5)'}>✕</button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}
          onClick={()=>{setShowModal(false);setNewName('');}}>
          <div style={{background:'#1a2235',border:'1px solid rgba(59,130,246,0.4)',borderRadius:16,padding:32,width:400,boxShadow:'0 20px 60px rgba(0,0,0,0.5)'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:700,marginBottom:8,color:'#fff'}}>Новый аудит</div>
            <div style={{fontSize:12,color:'rgba(200,215,235,0.5)',marginBottom:20}}>Введите название для нового аудита карточки</div>
            <input value={newName} onChange={e=>setNewName(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&createAudit()}
              placeholder="Например: Ветровки осень 2026"
              autoFocus
              style={{background:'#0a0f1a',border:'1px solid rgba(59,130,246,0.4)',borderRadius:8,color:'#fff',fontSize:14,padding:'10px 14px',width:'100%',boxSizing:'border-box',marginBottom:16,outline:'none'}}/>
            <div style={{display:'flex',gap:8}}>
              <button onClick={createAudit}
                style={{flex:1,background:'rgba(59,130,246,0.3)',border:'1px solid rgba(59,130,246,0.5)',color:'#60a5fa',fontSize:14,padding:'10px',borderRadius:8,cursor:'pointer',fontWeight:700}}>
                Создать
              </button>
              <button onClick={()=>{setShowModal(false);setNewName('');}}
                style={{flex:1,background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',color:'rgba(200,215,235,0.6)',fontSize:14,padding:'10px',borderRadius:8,cursor:'pointer'}}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
